import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ChevronRight, Car, CheckCircle2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import heroImage from "@/assets/blog/luxury-suv-vs-limo-hero.jpg";

const LuxurySuvVsLimoSanAntonio = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const canonical =
    "https://ricksblackcarservicesantonio.com/blog/luxury-suv-vs-limo-san-antonio";

  return (
    <>
      <Helmet>
        <title>Luxury SUV vs Limo Service in San Antonio | Rick's BCS</title>
        <meta
          name="description"
          content="Comparing a 2025 Audi Q7 Black Optic SUV to a traditional stretch limo for San Antonio weddings, corporate travel, and special events. Which is right for you?"
        />
        <meta
          name="keywords"
          content="limo service san antonio, luxury suv san antonio, audi q7 chauffeur, wedding limo san antonio, black car vs limo, executive transportation"
        />
        <link rel="canonical" href={canonical} />

        <meta property="og:title" content="Luxury SUV vs Limo: Which Is Right for San Antonio?" />
        <meta
          property="og:description"
          content="A side-by-side comparison of the 2025 Audi Q7 Black Optic and traditional stretch limousines for weddings, corporate events, and nights out."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: "Luxury SUV vs Traditional Limo: Which Is Right for Your San Antonio Event?",
            description:
              "A practical comparison of a 2025 Audi Q7 Black Optic SUV chauffeur service versus a traditional stretch limousine in San Antonio.",
            author: { "@type": "Person", name: "Rick Ruiz-Estrada" },
            publisher: {
              "@type": "Organization",
              name: "Rick's Black Car Service",
              logo: {
                "@type": "ImageObject",
                url: "https://ricksblackcarservicesantonio.com/og-image.jpg",
              },
            },
            datePublished: "2026-06-17",
            dateModified: "2026-06-17",
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            image: canonical.replace(/\/blog\/.+/, "/og-image.jpg"),
            keywords:
              "limo service san antonio, luxury suv san antonio, audi q7 chauffeur, wedding limo, executive transportation",
          })}
        </script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background pt-20">
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background z-10" />
          <img
            src={heroImage}
            alt="2025 Audi Q7 Black Optic SUV next to a black stretch limousine on a downtown San Antonio street at dusk"
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={1080}
          />

          <div className="relative z-20 container mx-auto px-4 text-center">
            <motion.div {...fadeInUp}>
              <nav className="flex items-center justify-center space-x-2 text-sm text-muted-foreground mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-foreground">Luxury SUV vs Limo</span>
              </nav>

              <h1 className="font-playfair text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 leading-tight">
                Luxury SUV vs. Traditional Limo
              </h1>
              <p className="text-xl md:text-2xl text-primary font-light max-w-3xl mx-auto">
                Which Is Right for Your San Antonio Event?
              </p>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto font-serif text-lg">
                A practical comparison of the 2025 Audi Q7 Black Optic and the classic stretch limousine for weddings, corporate travel, and nights out in San Antonio.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Article */}
        <div className="container mx-auto px-4 py-12 lg:py-16 max-w-4xl">
          <article className="prose prose-lg prose-invert max-w-none font-serif text-foreground/90 leading-relaxed space-y-6">
            <p className="text-xl">
              "I need a limo service in San Antonio." It's one of the most common requests we hear — and almost every time, what the client actually wants isn't a stretch limousine at all. They want a quiet, dignified ride in something nicer than a rideshare, driven by a professional who knows the city.
            </p>

            <p>
              The traditional stretch limo had its moment. Today, most luxury chauffeur companies in San Antonio — including ours — have moved to the modern luxury SUV. Here is an honest look at the trade-offs so you can decide which is right for your event.
            </p>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">The Quick Comparison</h2>

            <div className="not-prose grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-card border border-primary/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-6 h-6 text-primary" />
                  <h3 className="font-playfair text-xl font-bold text-foreground m-0">2025 Audi Q7 Black Optic</h3>
                </div>
                <ul className="space-y-2 text-foreground/90">
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Seats up to 6 passengers comfortably</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Low-profile, modern luxury aesthetic</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Easy entry/exit in formal attire</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Fits in standard parking, drives any route</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Authorized JBSA base access</span></li>
                </ul>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-6 h-6 text-muted-foreground" />
                  <h3 className="font-playfair text-xl font-bold text-foreground m-0">Traditional Stretch Limo</h3>
                </div>
                <ul className="space-y-2 text-foreground/90">
                  <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" /><span>Seats 8–10 for large parties</span></li>
                  <li className="flex gap-2"><XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" /><span>Dated party-bus aesthetic</span></li>
                  <li className="flex gap-2"><XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" /><span>Awkward to enter and exit gracefully</span></li>
                  <li className="flex gap-2"><XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" /><span>Can't navigate tight downtown streets</span></li>
                  <li className="flex gap-2"><XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" /><span>Most fleets are 10+ years old</span></li>
                </ul>
              </div>
            </div>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">For Weddings</h2>
            <p>
              Modern wedding photography favors clean, editorial-style images. A black Audi Q7 in front of a stone chapel or hill-country venue photographs beautifully — the lines are sharp and contemporary. A long white stretch limo, by comparison, dates a photo the moment it's taken.
            </p>
            <p>
              There's also a practical issue: brides in full gowns and bridesmaids in heels often struggle to climb in and out of a stretch limo's low rear bench. The Q7's elevated SUV seat height makes entry effortless, even in formal attire.
            </p>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">For Corporate and Executive Travel</h2>
            <p>
              No executive arrives at a downtown San Antonio board meeting in a stretch limo. The look is wrong. A black luxury SUV with a professionally dressed chauffeur is the global standard for executive transportation — discreet, fast through traffic, and easy to park near any office or hotel entrance.
            </p>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">For a Night Out or Special Event</h2>
            <p>
              Heading to a Spurs game at the Frost Bank Center, a concert at the Majestic, or dinner on the River Walk? An SUV gets you door-to-door without circling the block looking for a spot a stretch limo can physically fit into. We can also handle multiple stops on an hourly basis without the awkward staging a limo requires.
            </p>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">When a Limo Still Makes Sense</h2>
            <p>
              Honest answer: parties of 8+ people who want to stay in one vehicle together (think bachelorette parties or quinceañera entourages). If that's your situation, a stretch limo or party bus is the right tool. For everything else — 1 to 6 passengers wanting an elegant, professional ride — the Q7 wins.
            </p>

            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mt-12">Our Take</h2>
            <p>
              We chose the 2025 Audi Q7 Black Optic as our entire fleet for a reason. It delivers the discretion executives expect, the elegance weddings deserve, and the practicality San Antonio's mix of downtown, hill country, and military-base destinations demands — all in one vehicle.
            </p>
          </article>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-slate-800 via-slate-900 to-background border border-primary/30 rounded-2xl p-8 text-center"
          >
            <h3 className="font-playfair text-2xl font-bold text-foreground mb-3">
              Ready to Ride in Something Better Than a Limo?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Book the 2025 Audi Q7 Black Optic with a veteran-owned chauffeur service trusted across San Antonio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="btn-luxury">
                <a
                  href="https://ricksblackcarservicesantonio.square.site/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book Now <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/fleet">View Our Fleet</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default LuxurySuvVsLimoSanAntonio;